<template>
  <div>
    <div class="card border-0 shadow-sm" style="border-radius: 14px; background: #ffffff; border: 1.5px solid #e2e8f0 !important; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);">
      <!-- Standard Vertex Header -->
      <div class="card-header bg-white border-0 pt-3 pb-2 px-3 d-flex justify-content-between align-items-start align-items-sm-center flex-wrap gap-2" style="border-bottom: 1px solid #f1f5f9 !important;">
        <div class="d-flex align-items-center gap-2">
          <div class="widget-icon-pill" style="background-color: #E0F7FA; color: #00A0C1; width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 0.95rem; flex-shrink: 0;">
            <i class="fas fa-bug"></i>
          </div>
          <div>
            <h6 class="mb-0 fw-bold" style="color: #1e293b; font-size: 0.875rem;">Penetration Testing Summary</h6>
            <small class="text-muted" style="color: #64748b; font-size: 0.72rem;">Vulnerability assessment and security findings</small>
          </div>
        </div>
        <div class="widget-controls d-flex align-items-center gap-2">
          <span v-if="isError" class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle px-2 py-1" style="font-size: 11px; font-weight: 600; border-radius: 6px;">
            <i class="fas fa-exclamation-triangle me-1"></i>Service Unavailable
          </span>
          <button
            v-if="canAccessPentest"
            type="button"
            class="btn-pentest-link"
            @click.stop="redirectToPentest()"
            v-tooltip.top="'Open Pentester Platform'"
          >
            <span>Open Platform</span>
            <i class="fas fa-external-link-alt ms-1" style="font-size: 10px;"></i>
          </button>
        </div>
      </div>

      <div class="card-body p-3" @click="redirectToPentest()" style="cursor: pointer;">
        <div class="table-responsive" v-if="!isError && projectsList.length != 0">
          <table class="table pentest-table align-middle text-center mb-0">
            <thead>
              <tr class="pentest-table-header">
                <th class="text-start" style="padding-left: 14px;">Test</th>
                <th>Status</th>
                <th class="th-severity"><span class="badge-severity vtx-insignificant">Insignificant</span></th>
                <th class="th-severity"><span class="badge-severity vtx-low">Low</span></th>
                <th class="th-severity"><span class="badge-severity vtx-medium">Medium</span></th>
                <th class="th-severity"><span class="badge-severity vtx-high">High</span></th>
                <th class="th-severity"><span class="badge-severity vtx-veryhigh">Very High</span></th>
                <th>Total Vulns</th>
                <th style="padding-right: 14px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="list in projectsList" :key="list.ID" class="pentest-row">
                <td class="text-start fw-semibold text-slate" style="padding-left: 14px; width: 22%;">
                  {{ list.title }}
                </td>
                <td>
                  <span class="badge bg-light text-secondary border px-2 py-1" style="font-size: 11px;">Active</span>
                </td>
                <td class="fw-semibold text-slate">{{ list.insignificant }}</td>
                <td class="fw-semibold text-slate">{{ list.low }}</td>
                <td class="fw-semibold text-slate">{{ list.medium }}</td>
                <td class="fw-semibold text-slate">{{ list.high }}</td>
                <td class="fw-semibold text-slate">{{ list.very_high }}</td>
                <td>
                  <span class="badge rounded-pill bg-light text-dark border px-2 py-1 fw-bold">{{ list.report_total }}</span>
                </td>
                <td style="padding-right: 14px;">
                  <span class="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle px-2 py-1 fw-bold">{{ list.action_total }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Service Down / Error State -->
        <div v-if="isError" class="py-4 text-center">
          <div class="mb-2">
            <div class="mx-auto rounded-circle d-flex align-items-center justify-content-center" style="width: 48px; height: 48px; background-color: #fef3c7; color: #d97706;">
              <i class="fas fa-server" style="font-size: 20px;"></i>
            </div>
          </div>
          <p class="mb-1 fw-semibold text-slate" style="font-size: 14px;">
            {{ errorMessage || 'Pentester service is not reached or down.' }}
          </p>
          <small class="text-muted" style="font-size: 12px;">
            The integrated pentester platform is currently unavailable. Dashboard access remains active.
          </small>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && projectsList.length == 0" class="py-4 text-center">
          <p class="text-muted mb-0" style="font-size: 13px;">No penetration tests have been initiated to date.</p>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="py-4 text-center">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-2 mb-0" style="font-size: 13px;">Loading penetration tests...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Pentest Summary',
  props: ['organisationID', 'showProduct'],

   data() {
      return {
        projectsList: [],
        loading: false,
        isError: false,
        errorMessage: ''
      }
   },

   computed: {
     canAccessPentest() {
       const auth = this.$store?.state?.auth;
       if (!auth) return false;
       return !!(auth.isPentester ||
                 auth.isPentestAdmin ||
                 auth.isSuperAdmin ||
                 auth.isOrganisationAdmin ||
                 (auth.accessType && auth.accessType === 'vertex_analyst'));
     }
   },

   async created() {
    if (process.env.NODE_ENV != "development" && this.showProduct) {
      await this.init()
    }
   },

   watch: {
    organisationID() {
      if (this.showProduct) {
        this.init()
      }
    },
    showProduct(newVal) {
      if (newVal && this.projectsList.length === 0 && !this.isError) {
        this.init()
      }
    }
   },

   methods: {
      init() {
        if (!process.env.VUE_APP_API_PENTEST_URL || !this.organisationID) {
          return
        }

        this.loading = true
        this.isError = false
        this.errorMessage = ''

        this.$axios.get(process.env.VUE_APP_API_PENTEST_URL + '/organisation/'+ this.organisationID + '/project', {
          skipAuthRefresh: true
        })
        .then(res => {
          if (res && res.data && res.data.success) {
            this.projectsList = res.data.data || []
          } else {
            this.projectsList = []
          }
        })
        .catch(err => {
          console.warn('Pentester platform unreachable:', err)
          this.isError = true
          if (err.response) {
            if (err.response.status === 401 || err.response.status === 403) {
              this.errorMessage = 'Pentester service is not reached or unauthorized.'
            } else if (err.response.status >= 500) {
              this.errorMessage = 'Pentester service is down or temporarily unavailable.'
            } else {
              this.errorMessage = 'Pentester service is not reached or unavailable.'
            }
          } else {
            this.errorMessage = 'Pentester service is not reached or down.'
          }
        })
        .finally(() => {
          this.loading = false
        })
      },

    redirectToPentest() {
        const auth = this.$store.state.auth;
        const canAccess = auth.isPentester ||
                          auth.isPentestAdmin ||
                          auth.isSuperAdmin ||
                          auth.isOrganisationAdmin ||
                          (auth.accessType && auth.accessType === 'vertex_analyst');

        if (!canAccess) {
          return;
        }

        const pentesterUrl = localStorage.getItem('pentester_url');

        if (!pentesterUrl)
          return;

        // Validate that pentesterUrl is a valid http:// or https:// URL to prevent javascript: or invalid schemes
        try {
          const parsed = new URL(pentesterUrl, window.location.origin);
          if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            console.warn('Blocked invalid pentester_url scheme:', parsed.protocol);
            return;
          }
          // Open the final redirect URL in a new tab with noopener,noreferrer
          window.open(parsed.href, '_blank', 'noopener,noreferrer');
        } catch (e) {
          console.warn('Invalid pentester_url format:', e);
        }
      }
    }
}
</script>

<style scoped>
.widget-icon-pill {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.text-slate {
  color: #1e293b;
}

.pentest-table-header th {
  background-color: #F4F6F8;
  color: #435666;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 8px 8px;
  border-bottom: 1px solid #e2e8f0;
}

.th-severity {
  padding: 6px 0 !important;
}

.pentest-row td {
  padding: 10px 8px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8125rem;
}

.pentest-row:hover td {
  background-color: #f8fafc;
}

.badge-severity {
  display: block;
  width: 100%;
  padding: 4px 6px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #ffffff !important;
  text-align: center;
}

.vtx-insignificant {
  color: #ffffff !important;
  background-color: #00B843 !important;
  border-radius: 6px 0 0 6px;
}

.vtx-low {
  color: #ffffff !important;
  background-color: #C4D600 !important;
  border-radius: 0;
}

.vtx-medium {
  color: #ffffff !important;
  background-color: #FF9F43 !important;
  border-radius: 0;
}

.vtx-high {
  color: #ffffff !important;
  background-color: #FF5B24 !important;
  border-radius: 0;
}

.vtx-veryhigh {
  color: #ffffff !important;
  background-color: #EB1C24 !important;
  border-radius: 0 6px 6px 0;
}

.btn-pentest-link {
  background-color: #ffffff;
  color: #00A0C1;
  border: 1.5px solid #00A0C1;
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.btn-pentest-link:hover {
  background-color: #00A0C1;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.35);
}
</style>
